import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { DATA_TABLE_SOURCE_AGENT_LEADS } from 'common/dataTableConstants';
import { LEAD_STATUS_LIST } from 'common/leadConstants';
import {
  APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE,
  APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE,
  createLeadTableSections,
  getLeadDataTableSettingsFromProps,
  getLeadsUpdated,
} from 'common/apartmentLocatorHelpers';

import SrAgentLeadsSection from 'client/components/SrAgentLeadsSection';
import SrAgentLeadsSectionPastMonths from 'client/components/SrAgentLeadsSectionPastMonths';

import ApartmentLocatorActions from 'client/actions/ApartmentLocator';
import { dataTable, actionDataTableLoadItems } from 'client/helpers/dataTables';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import branch from 'client/core/branch';

@branch((props) => ({
  currentUser: ['currentUser'],
  dataTable: ['dataTables', DATA_TABLE_SOURCE_AGENT_LEADS],
  leadsUpdated: ['apartmentLocator', 'leadsUpdated'],
  userMoveInDatePastMonthsRange: [
    'apartmentLocator',
    'leadsByStatus',
    props.leadStatus,
    'userMoveInDatePastMonthsRange',
  ],
  shouldLoadMorePastMonths: [
    'apartmentLocator',
    'leadsByStatus',
    props.leadStatus,
    'shouldLoadMorePastMonths',
  ],
}))
@dataTable(getLeadDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 32,
})
class SrAgentLeadsList extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    leadsUpdated: PropTypes.object.isRequired,
    leadStatus: PropTypes.oneOf(LEAD_STATUS_LIST).isRequired,
    searchText: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    sortCriteria: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    dataTable: PropTypes.object,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    onUpdateLeadStatus: PropTypes.func.isRequired,
    userMoveInDatePastMonthsRange: PropTypes.array.isRequired,
    shouldLoadMorePastMonths: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super();

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadItems, getLeadDataTableSettingsFromProps(this.props));
    }
  };

  _reloadDataTableAgentLeads = ({ renterUserId, showPositiveFeedbackModal }) => {
    const { dispatch, currentUser, onUpdateLeadStatus } = this.props;
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
      viewId: `leads-id-${renterUserId}`,
      filter: {
        apartmentLocatorUserId: currentUser.id,
        renterUserId: renterUserId,
      },
      count: 1,
    }).then(() => onUpdateLeadStatus({ showPositiveFeedbackModal }));
  };

  _onLoadPastMonths = () => {
    const { dispatch, leadStatus, searchText, sortCriteria } = this.props;
    dispatch(ApartmentLocatorActions.addUserMoveInDatePastMonthRange, {
      leadStatus,
      searchText,
      sortCriteria,
    });
  };

  render() {
    const {
      onInfiniteScrollWrapperRef,
      leadsUpdated,
      leadStatus,
      dataTable,
      dataTableApiState,
      dispatch,
      searchText,
      sortCriteria,
      currentUser,
      userMoveInDatePastMonthsRange,
      shouldLoadMorePastMonths,
    } = this.props;

    const items = [...this.props.items];
    const itemRenterIds = items.map(({ renterUserId }) => renterUserId);
    const { leadsAddedToStep, leadsRemovedFromStep } = getLeadsUpdated({ leadsUpdated, leadStatus });
    if (leadsRemovedFromStep.length) {
      leadsRemovedFromStep.forEach((renterUserId) => {
        const item = dataTable && dataTable.items.find(
          (lead) => lead.renterUserId === renterUserId
        );
        if (item && !itemRenterIds.includes(item.renterUserId)) {
          items.push(item);
        }
      });
    }

    const sections = createLeadTableSections({
      items,
      leadStatus,
      sortCriteria,
      leadsRemovedFromStep,
    });
    const sectionProps = {
      showPhoneNumber: !!searchText,
      leadsRemovedFromStep: leadsRemovedFromStep,
      leadsAddedToStep: leadsAddedToStep,
      reloadTable: this._reloadDataTableAgentLeads,
      dispatch: dispatch,
    };

    return (
      <div className='listContainer' ref={onInfiniteScrollWrapperRef}>
        {dataTableApiState.inProgress && !items.length &&
          <div className='mobilePullDown' />
        }

        {(sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE
          || sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE)
          && (
            <div>
              {shouldLoadMorePastMonths && (
                <div className='viewPastContainer' onClick={this._onLoadPastMonths}>
                  <span className='text'>View past months</span>
                </div>
              )}
              {userMoveInDatePastMonthsRange.map((userMoveInDateRangePastMonth) => (
                <SrAgentLeadsSectionPastMonths
                  key={userMoveInDateRangePastMonth.join('_')}
                  sortCriteria={sortCriteria}
                  userMoveInDateRange={userMoveInDateRangePastMonth}
                  currentUser={currentUser}
                  leadStatus={leadStatus}
                  searchText={searchText}
                  leadsRemovedFromStep={leadsRemovedFromStep}
                  sectionProps={sectionProps}
                />
              ))}
            </div>
          )}

        {sections.map(({ id, title, items }) => (
          <SrAgentLeadsSection
            key={id}
            leadStatus={leadStatus}
            title={title}
            items={items}
            {...sectionProps}
          />
        ))}

        {dataTableApiState.inProgress && !!items.length && (
          <div className='mobilePullDown bottom' />
        )}
      </div>
    );
  }
}

SrAgentLeadsList.displayName = 'SrAgentLeadsList';

export default SrAgentLeadsList;
